import React, { useState } from "react";
import { AUTH_STORAGE_KEY, SERVER_URL } from "../socket";

// Step 1 — Select contacts
// Step 2 — Set group name & icon → Create

function CreateGroupModal({ contacts, setShowCreateGroup, handleCreateGroup }) {
  const [step,            setStep]            = useState(1);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [search,          setSearch]          = useState("");
  const [groupName,       setGroupName]       = useState("");
  const [groupImage,      setGroupImage]      = useState("");
  const [uploading,       setUploading]       = useState(false);
  const [error,           setError]           = useState("");

  // ── helpers ────────────────────────────────────────────────────────────────
  const toggleMember = (email) =>
    setSelectedMembers((prev) =>
      prev.includes(email) ? prev.filter((m) => m !== email) : [...prev, email]
    );

  const filteredContacts = contacts.filter((c) => {
    const email = (c.emails?.[0] || "").toLowerCase();
    const name  = (c.name       || "").toLowerCase();
    const q     = search.toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const fd     = new FormData();
      fd.append("file", file);
      const res  = await fetch(`${SERVER_URL}/api/groups/upload`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${parsed.token}` },
        body:    fd,
      });
      const body = await res.json();
      setGroupImage(body.url || "");
    } catch (e) {
      console.error("Image upload failed", e);
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    if (selectedMembers.length === 0) {
      setError("Select at least one member.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleCreate = () => {
    if (!groupName.trim()) {
      setError("Group name is required.");
      return;
    }
    setError("");
    handleCreateGroup(groupName.trim(), selectedMembers, groupImage);
  };

  // ── shared header ──────────────────────────────────────────────────────────
  const StepDots = () => (
    <div style={{ display: "flex", justifyContent: "center", gap: 8, margin: "4px 0 16px" }}>
      {[1, 2].map((s) => (
        <div
          key={s}
          style={{
            width: 10, height: 10, borderRadius: "50%",
            background: s === step ? "#1e40af" : "#cbd5e1",
            transition: "background .2s",
          }}
        />
      ))}
    </div>
  );

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="contacts-form-overlay"
      onClick={() => setShowCreateGroup(false)}
    >
      <div
        className="contacts-form"
        style={{ maxWidth: 440, width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── STEP 1 — Select contacts ─────────────────────────────────────── */}
        {step === 1 && (
          <>
            <h3 className="form-title">New Group</h3>
            <StepDots />

            <div className="form-body">

              {/* Search */}
              <div className="input-group" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  placeholder=" "
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
                <label>Search contacts</label>
              </div>

              {/* Selected chips */}
              {selectedMembers.length > 0 && (
                <div style={{
                  display: "flex", flexWrap: "wrap", gap: 6,
                  marginBottom: 10, padding: "6px 0", maxHeight: 80, overflowY: "auto"
                }}>
                  {selectedMembers.map((email) => {
                    const c    = contacts.find((c) => c.emails?.[0] === email);
                    const name = c?.name || email.split("@")[0];
                    return (
                      <span
                        key={email}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "#eff6ff", color: "#1e40af",
                          borderRadius: 20, padding: "3px 10px", fontSize: 13,
                          border: "1px solid #bfdbfe",
                        }}
                      >
                        {name}
                        <span
                          style={{ cursor: "pointer", fontWeight: 700 }}
                          onClick={() => toggleMember(email)}
                        >
                          ×
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Contact list */}
              <div
                className="memberList"
                style={{ maxHeight: 320, overflowY: "auto" }}
              >
                {filteredContacts.length === 0 && (
                  <p style={{ textAlign: "center", color: "#94a3b8", padding: 16 }}>
                    No contacts found
                  </p>
                )}

                {filteredContacts.map((c) => {
                  const email    = c.emails?.[0] || "";
                  const name     = c.name || email;
                  const selected = selectedMembers.includes(email);
                  const initial  = name.charAt(0).toUpperCase();

                  return (
                    <div
                      key={email}
                      className={`memberItem ${selected ? "selected" : ""}`}
                      onClick={() => toggleMember(email)}
                      style={{ cursor: "pointer" }}
                    >
                      {/* Avatar */}
                      <div
                        className="avatar-letter"
                        style={{
                          width: 38, height: 38, borderRadius: "50%",
                          background: selected ? "#1e40af" : "#e2e8f0",
                          color:      selected ? "#fff"    : "#475569",
                          display: "flex", alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600, fontSize: 15, flexShrink: 0,
                          transition: "background .15s, color .15s",
                        }}
                      >
                        {selected ? "✔" : initial}
                      </div>

                      {/* Info */}
                      <div className="memberInfo" style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{name}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>{email}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {error && (
                <p style={{ color: "#ef4444", fontSize: 13, marginTop: 8 }}>{error}</p>
              )}

              <div className="form-buttons" style={{ marginTop: 16 }}>
                <button className="btn-primary" onClick={handleNext}>
                  Next → ({selectedMembers.length} selected)
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setShowCreateGroup(false)}
                >
                  Cancel
                </button>
              </div>

            </div>
          </>
        )}

        {/* ── STEP 2 — Group name & icon ───────────────────────────────────── */}
        {step === 2 && (
          <>
            <h3 className="form-title">Group Details</h3>
            <StepDots />

            <div className="form-body">

              {/* Group icon upload */}
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
                <label
                  htmlFor="groupImageInput"
                  style={{
                    width: 90, height: 90, borderRadius: "50%",
                    background: "#f1f5f9",
                    border: "2px dashed #cbd5e1",
                    display: "flex", alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer", overflow: "hidden",
                    position: "relative",
                  }}
                  title="Upload group icon"
                >
                  {uploading ? (
                    <span style={{ fontSize: 24 }}>⏳</span>
                  ) : groupImage ? (
                    <img
                      src={`${SERVER_URL}${groupImage}`}
                      alt="group icon"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{ fontSize: 32, lineHeight: 1 }}>📷</span>
                  )}

                  {/* Camera overlay on hover */}
                  <div style={{
                    position: "absolute", bottom: 0, width: "100%",
                    textAlign: "center", background: "rgba(0,0,0,.35)",
                    color: "#fff", fontSize: 11, padding: "3px 0",
                  }}>
                    edit
                  </div>
                </label>

                <input
                  id="groupImageInput"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => uploadImage(e.target.files[0])}
                />
              </div>

              {/* Group name */}
              <div className="input-group" style={{ marginBottom: 16 }}>
                <input
                  type="text"
                  placeholder=" "
                  value={groupName}
                  onChange={(e) => { setGroupName(e.target.value); setError(""); }}
                  autoFocus
                />
                <label>Group Name</label>
              </div>

              {/* Member count summary */}
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 4 }}>
                👥 {selectedMembers.length} member{selectedMembers.length !== 1 ? "s" : ""} selected
              </p>

              {/* Chips */}
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14,
              }}>
                {selectedMembers.map((email) => {
                  const c    = contacts.find((c) => c.emails?.[0] === email);
                  const name = c?.name || email.split("@")[0];
                  return (
                    <span
                      key={email}
                      style={{
                        background: "#eff6ff", color: "#1e40af",
                        borderRadius: 20, padding: "3px 10px",
                        fontSize: 12, border: "1px solid #bfdbfe",
                      }}
                    >
                      {name}
                    </span>
                  );
                })}
              </div>

              {error && (
                <p style={{ color: "#ef4444", fontSize: 13, marginBottom: 8 }}>{error}</p>
              )}

              <div className="form-buttons">
                <button
                  className="btn-secondary"
                  onClick={() => { setStep(1); setError(""); }}
                >
                  ← Back
                </button>
                <button className="btn-primary" onClick={handleCreate}>
                  Create Group ✔
                </button>
              </div>

            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default CreateGroupModal;
